import classNames from "classnames";

import type { PaymentSource } from "../../../hooks/useBilling";
import type { Estimate } from "../../../hooks/useCheckout";
import type { SeatBasedTeamSubscription } from "../../../hooks/useTeamsTypes";
import useTranslation from "../../../hooks/useTranslation";
import { ChargebeePaymentType } from "../../../lib/ChargebeeAdapter";
import { formatDueOnDate } from "../../../utils/convertUtils";
import { CheckoutSummaryRow } from "../../ChargebeeCheckout/components/CheckoutSummaryRow/CheckoutSummaryRow";
import { EstimateSummaryPeriod } from "../EstimateSummaryPeriod/EstimateSummaryPeriod";

import style from "./SeatUpgradeEstimateSummary.module.css";

export const SeatUpgradeEstimateSummary: React.FC<{
  proratedEstimate?: Estimate;
  renewalEstimate: Estimate;
  renewalDate: string;
  currencySymbol?: string;
  isLoading: boolean;
  additionalEditorSeatCount: number;
  subscription: SeatBasedTeamSubscription;
  paymentSource: PaymentSource;
}> = ({
  proratedEstimate,
  renewalEstimate,
  renewalDate,
  currencySymbol,
  isLoading,
  additionalEditorSeatCount,
  subscription,
  paymentSource,
}) => {
  const { t, m } = useTranslation();
  const inviteText = t(m.EstimateSummary.Invite);
  return (
    <>
      <div className={style.summary}>
        <span className={style.summaryTitle}>{t(m.EstimateSummary.Summary)}</span>
        {subscription.isTrial && renewalEstimate.trialEnd && (
          <>
            <CheckoutSummaryRow
              name={t(m.EstimateSummary.DueOnWithTax, {
                date: formatDueOnDate(renewalEstimate.trialEnd),
              })}
              price={renewalEstimate.totalAmount}
              currencySymbol={currencySymbol}
              isLoading={isLoading}
            />
            <div className={classNames(style.tertiary, style.priceBreakdown)}>
              <span>
                {t(m.EstimateSummary.EditorSeatBreakdown, {
                  count: renewalEstimate.editorSeatCount - 1,
                  price: `${currencySymbol}${renewalEstimate.unitAmount}`,
                })}
              </span>
              {" × "}
              <EstimateSummaryPeriod subscriptionPeriod={renewalEstimate.period} />
              <br />
              <span>
                {t(m.EstimateSummary.OwnerSeatBreakdown, {
                  price: `${currencySymbol}${renewalEstimate.unitAmount}`,
                })}
              </span>
              {" × "}
              <EstimateSummaryPeriod subscriptionPeriod={renewalEstimate.period} />
            </div>
            <div className={style.trialWrapper}>
              <hr className={style.horizontalLine} />
              <CheckoutSummaryRow
                isPriceHighlighted={true}
                name={t(m.EstimateSummary.TotalDueToday)}
                price={proratedEstimate?.totalAmount ?? "0"}
                currencySymbol={currencySymbol}
              />
            </div>
          </>
        )}
        {!subscription.isTrial && (
          <>
            <CheckoutSummaryRow
              name={t(m.EstimateSummary.DueTodayWithTax)}
              price={proratedEstimate?.totalAmount}
              priceTooltipText={t(m.EstimateSummary.PriceProratedTooltip)}
              currencySymbol={currencySymbol}
              isLoading={isLoading}
            />
            <div className={classNames(style.tertiary, style.priceBreakdown)}>
              <span>
                {t(m.EstimateSummary.EditorSeatBreakdown, {
                  count: additionalEditorSeatCount,
                  price: `${currencySymbol}${renewalEstimate.unitAmount}`,
                })}
              </span>
              {" × "}
              <EstimateSummaryPeriod
                subscriptionPeriod={renewalEstimate.period}
                partialDurationMs={new Date(renewalDate).getTime() - Date.now()}
              />
            </div>
          </>
        )}
      </div>
      {!renewalEstimate.trialEnd && (
        <p className={classNames(style.tertiary, style.additionalInfo)}>
          {paymentSource.type === ChargebeePaymentType.Card && (
            <span>
              {t(m.EstimateSummary.CardChargedOnClick, {
                lastFourDigits: paymentSource.lastFourDigits,
                submitButtonText: inviteText,
              })}
            </span>
          )}
          {paymentSource.type === ChargebeePaymentType.ApplePay && (
            <span>
              {t(m.EstimateSummary.ApplePayChargedOnClick, { submitButtonText: inviteText })}
            </span>
          )}
          <br />
          <span>{t(m.EstimateSummary.AllSeatsRenewAtFullPrice)}</span>{" "}
          <span>{t(m.EstimateSummary.CardChargedRenewal, { period: renewalEstimate.period })}</span>{" "}
          <span>
            {t(m.EstimateSummary.NextPaymentAmount, {
              amount: `${currencySymbol}${renewalEstimate.totalAmount}`,
            })}
          </span>
          {renewalEstimate.currencyCode === "USD" && (
            <>
              {" "}
              <span>
                {t(m.EstimateSummary.AllPricesIn, { currencyCode: renewalEstimate.currencyCode })}
              </span>
            </>
          )}
        </p>
      )}
      {renewalEstimate.trialEnd && (
        <p className={classNames(style.tertiary, style.additionalInfo)}>
          <span>
            {t(m.EstimateSummary.TrialEndsOn, {
              date: formatDueOnDate(renewalEstimate.trialEnd),
            })}
          </span>{" "}
          <span>{t(m.EstimateSummary.AllSeatsRenewAtFullPrice)}</span>{" "}
          {paymentSource.type === ChargebeePaymentType.Card && (
            <>
              <span>
                {t(m.EstimateSummary.CardChargedAfterTrial, {
                  lastFourDigits: paymentSource.lastFourDigits,
                  period: renewalEstimate.period,
                })}
              </span>{" "}
            </>
          )}
          {paymentSource.type === ChargebeePaymentType.ApplePay && (
            <>
              <span>
                {t(m.EstimateSummary.ApplePayChargedAfterTrial, {
                  period: renewalEstimate.period,
                })}
              </span>{" "}
            </>
          )}
          <span>
            {t(m.EstimateSummary.NextPaymentAmount, {
              amount: `${currencySymbol}${renewalEstimate.totalAmount}`,
            })}
          </span>
          {renewalEstimate.currencyCode === "USD" && (
            <>
              {" "}
              <span>
                {t(m.EstimateSummary.AllPricesIn, { currencyCode: renewalEstimate.currencyCode })}
              </span>
            </>
          )}
        </p>
      )}
    </>
  );
};
